import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { authenticator } from 'otplib';
import { getFromStorage, saveToStorage, clearSecureStorage } from '../utils/storage';
import { decryptData } from '../utils/crypto';
import { 
  getAvoBalance, 
  sendAvoTransaction, 
  getCurrentAvoNetwork, 
  estimateAvoGasCost, 
  getAvoPrice,
  getAvoProvider
} from '../utils/avoUtils';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  Container, Box, Typography, Card, CardContent, Button, TextField, AppBar, Toolbar,
  IconButton, MenuItem, CircularProgress, Fade, Chip, List, ListItem,
  ListItemIcon, ListItemText, FormControl, InputLabel, Select, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, styled, ThemeProvider
} from '@mui/material';
import strictMonochromeTheme from '../utils/strictMonochromeTheme';
import {
  Send as SendIcon, Wallet, Lock, Person, CurrencyExchange, Info, ContentCopy,
  Public, Security, AllInclusive, Key, Speed, Delete, ExitToApp, Warning, Visibility, VisibilityOff,
  TrendingUp, TrendingDown, TrendingFlat
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LoadingButton } from '@mui/lab';

// Styled card component con estilo estrictamente monocromático
const GlassCard = styled(Card)(({ theme }) => ({
  borderRadius: '0',
  backgroundColor: '#000000',
  border: '1px solid #FFFFFF',
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.77, 0, 0.175, 1)',
  '&:hover': {
    borderColor: '#FFFFFF',
    boxShadow: '0 0 15px rgba(255,255,255,0.1)'
  }
}));

// Styled button component with AVO monochromatic style
const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: '0',
  textTransform: 'uppercase',
  padding: '14px 28px',
  fontWeight: 700,
  letterSpacing: '2px',
  background: 'transparent',
  color: '#FFFFFF',
  border: '1px solid #FFFFFF',
  transition: 'all 0.3s cubic-bezier(0.77, 0, 0.175, 1)',
  '&:hover': { 
    background: '#FFFFFF', 
    color: '#000000',
    border: '1px solid #FFFFFF'
  },
  '&.Mui-disabled': {
    borderColor: '#424242',
    color: '#424242'
  }
}));

// Styled loading button
const GradientLoadingButton = styled(LoadingButton)(({ theme }) => ({
  borderRadius: '0',
  textTransform: 'uppercase',
  padding: '14px 28px',
  fontWeight: 700,
  letterSpacing: '2px',
  background: 'transparent',
  color: '#FFFFFF',
  border: '1px solid #FFFFFF',
  transition: 'all 0.3s cubic-bezier(0.77, 0, 0.175, 1)',
  '&:hover': { 
    background: '#FFFFFF', 
    color: '#000000',
    border: '1px solid #FFFFFF'
  },
  '&.Mui-disabled': {
    borderColor: '#424242',
    color: '#424242',
    border: '1px solid #424242'
  }
}));

// Dashboard card component
const DashboardCard = ({ children, sx = {} }) => (
  <GlassCard 
    elevation={0} 
    sx={{
      p: 3,
      minHeight: '100%',
      ...sx
    }}
  >
    <CardContent sx={{ p: 0, height: '100%' }}>
      {children}
    </CardContent>
  </GlassCard>
);

function Send({ wallet, setWallet, setIsSessionActive }) {
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [decryptedWalletJson, setDecryptedWalletJson] = useState(null);
  const [balance, setBalance] = useState('0');
  const [currentNetwork, setCurrentNetwork] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWalletValid, setIsWalletValid] = useState(false);
  const [isWalletLoading, setIsWalletLoading] = useState(false);
  const [gasOptions, setGasOptions] = useState(null);
  const [gasPriority, setGasPriority] = useState('medium');
  const [maxMode, setMaxMode] = useState(false);
  const [avoPrice, setAvoPrice] = useState(125.50);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Debug: Log props to ensure no toastProps is passed
  console.log('DEBUG: Props passed to Send:', { wallet, setWallet, setIsSessionActive });

  useEffect(() => {
    let timeoutId;

    const validateAndFetchData = async () => {
      if (!wallet || !wallet.address) {
        toast.error('No wallet provided. Create or import a wallet.', {
          position: 'top-center',
          style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        });
        setIsWalletValid(false);
        setIsWalletLoading(false);
        return;
      }

      if (wallet.address === 'loaded') {
        setIsWalletLoading(true);
        setIsWalletValid(false);

        timeoutId = setTimeout(() => {
          if (wallet.address === 'loaded') {
            toast.error('Wallet loading timed out.', {
              position: 'top-center',
              style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
            });
            setIsWalletLoading(false);
            setIsWalletValid(false);
          }
        }, 10000);

        return;
      }

      clearTimeout(timeoutId);

      if (!ethers.utils.isAddress(wallet.address)) {
        console.error('Invalid wallet address:', wallet.address);
        toast.error(`Invalid wallet address: ${wallet.address}`, {
          position: 'top-center',
          style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        });
        setIsWalletValid(false);
        setIsWalletLoading(false);
        return;
      }

      setIsWalletLoading(false);
      setIsWalletValid(true);

      try {
        const network = await getFromStorage('currentAvoNetwork') || 'devnet';
        // Suppress debug log in production
        if (process.env.NODE_ENV === 'development') {
          console.log('DEBUG: Network obtained:', network);
        }

        const defaultGasOptions = {
          low: { maxFeePerGas: '20000000000', maxPriorityFeePerGas: '2000000000', gasLimit: '21000', estimatedCost: '420000000000000' },
          medium: { maxFeePerGas: '20000000000', maxPriorityFeePerGas: '2000000000', gasLimit: '21000', estimatedCost: '420000000000000' },
          high: { maxFeePerGas: '20000000000', maxPriorityFeePerGas: '2000000000', gasLimit: '21000', estimatedCost: '420000000000000' },
        };

        const currentBalance = await getAvoBalance(wallet.address).catch(err => {
          console.error('Error fetching balance:', err);
          return '0';
        });

        console.log('DEBUG: Balance fetched for address:', wallet.address, 'balance:', currentBalance);

        const gasDataPromise = recipient && ethers.utils.isAddress(recipient) && parseFloat(currentBalance) > 0
          ? estimateAvoGasCost(wallet.address, recipient, network)
          : Promise.resolve(defaultGasOptions);

        const [balanceResult, networkName, gasData, priceData] = await Promise.all([
          Promise.resolve(currentBalance),
          getCurrentAvoNetwork(network).catch(err => {
            console.error('Error fetching network:', err);
            return 'AVO Devnet';
          }),
          gasDataPromise.catch(err => {
            console.error('Error fetching gas options:', err);
            return defaultGasOptions;
          }),
          getAvoPrice().catch(err => {
            console.error('Error fetching AVO price:', err);
            return { currentPrice: 3000 };
          }),
        ]);

        console.log('DEBUG: Data obtained - balance:', balanceResult, 'networkName:', networkName, 'gasData:', gasData, 'priceData:', priceData);

        setBalance(balanceResult || '0');
        setCurrentNetwork(networkName?.name || networkName || 'AVO Devnet');
        setGasOptions(gasData);
        setAvoPrice(priceData.currentPrice || 3000);
      } catch (err) {
        console.error('Error loading data:', err);
        toast.error(`Error loading data: ${err.message}`, {
          position: 'top-center',
          style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        });
      }
    };

    validateAndFetchData();

    return () => clearTimeout(timeoutId);
  }, [wallet, recipient]);

  const calculateMaxSendable = () => {
    try {
      const balanceWei = ethers.utils.parseEther(balance || '0');
      if (!gasOptions || !gasOptions[gasPriority]) {
        console.warn('DEBUG: Gas options not available');
        return '0';
      }

      const gasCostWei = ethers.BigNumber.from(gasOptions[gasPriority].estimatedCost).mul(2);

      if (balanceWei.lte(gasCostWei)) {
        console.warn('DEBUG: Insufficient balance to cover gas:', {
          balanceWei: balanceWei.toString(),
          gasCostWei: gasCostWei.toString(),
        });
        return '0';
      }

      const maxWei = balanceWei.sub(gasCostWei);
      const maxAvo = ethers.utils.formatEther(maxWei);
      console.log('DEBUG: Maximum sendable calculated:', maxAvo);
      return maxAvo;
    } catch (error) {
      console.error('Error calculating maximum sendable:', error);
      return '0';
    }
  };

  const handleMaxClick = () => {
    const maxAmount = calculateMaxSendable();
    if (maxAmount === '0') {
      toast.error('Insufficient balance to send after covering gas. Get AVO tokens from the AVO network.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
      return;
    }
    setAmount(maxAmount);
    setMaxMode(true);
  };

  const validateInputs = () => {
    if (!ethers.utils.isAddress(recipient)) {
      return 'Please enter a valid AVO address';
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return 'Amount must be a positive number';
    }

    const balanceNum = parseFloat(balance);
    const gasCostWei = gasOptions && gasOptions[gasPriority] ? ethers.BigNumber.from(gasOptions[gasPriority].estimatedCost) : ethers.BigNumber.from('420000000000000');
    const gasCostAvo = parseFloat(ethers.utils.formatEther(gasCostWei));
    const totalCost = amountNum + gasCostAvo;

    if (totalCost > balanceNum) {
      return `Insufficient balance. Available: ${balanceNum.toFixed(6)} AVO (including gas)`;
    }

    if (!password) {
      return 'Please enter the wallet password';
    }

    return null;
  };

  const validatePassword = async () => {
    try {
      const encryptedWalletKey = 'cryptoWallet';
      const encryptedWallet = await getFromStorage(encryptedWalletKey);

      if (!encryptedWallet) {
        toast.error('Wallet not found. Please create a new wallet.', {
          position: 'top-center',
          style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        });
        navigate('/create-wallet');
        throw new Error('Encrypted wallet data not found');
      }

      console.log('DEBUG: encryptedWallet:', encryptedWallet);
      const decrypted = await decryptData(encryptedWallet, password);
      console.log('DEBUG: decrypted:', decrypted);

      let decryptedJson;
      try {
        decryptedJson = JSON.parse(decrypted);
      } catch (parseError) {
        console.error('Error parsing decrypted data:', parseError);
        throw new Error('Decrypted wallet data is not valid JSON');
      }

      if (!decryptedJson || !decryptedJson.privateKey || !ethers.utils.isHexString(decryptedJson.privateKey)) {
        console.error('DEBUG: Invalid decryptedJson:', decryptedJson);
        throw new Error('Decrypted wallet does not contain a valid private key');
      }

      setDecryptedWalletJson(decryptedJson);
      setIsPasswordValid(true);
      toast.success('Password valid. Please enter the 2FA code.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
    } catch (err) {
      console.error('Error validating password:', err);
      toast.error(`Error: ${err.message}`, {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
      setIsPasswordValid(false);
      setDecryptedWalletJson(null);
    }
  };

  const validateTwoFACode = async (code) => {
    try {
      const twoFASecretKey = `2faSecret_${wallet.address}`;
      const secretData = await getFromStorage(twoFASecretKey);

      if (!secretData) {
        toast.error('2FA secret not found. Please set up 2FA again.', {
          position: 'top-center',
          style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        });
        navigate('/setup-2fa');
        throw new Error(`2FA secret not found (looking for: ${twoFASecretKey})`);
      }

      const isTwoFAValid = authenticator.verify({
        token: code,
        secret: secretData,
        window: 1,
      });

      if (!isTwoFAValid) {
        throw new Error('Invalid or expired 2FA code');
      }

      return true;
    } catch (err) {
      console.error('Error validating 2FA:', err);
      throw new Error(`2FA verification error: ${err.message}`);
    }
  };

  const handleSend = async () => {
    if (!isWalletValid || !wallet?.address) {
      toast.error('Wallet is not available.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
      return;
    }

    if (!isPasswordValid || !decryptedWalletJson) {
      toast.error('Please validate the password first.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
      return;
    }

    if (!twoFACode || !/^\d{6}$/.test(twoFACode)) {
      toast.error('Please enter a valid 6-digit 2FA code.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
      return;
    }

    setConfirmDialogOpen(true);
  };

  const confirmTransaction = async () => {
    setConfirmDialogOpen(false);
    setIsProcessing(true);
    try {
      const validationError = validateInputs();
      if (validationError) throw new Error(validationError);

      await validateTwoFACode(twoFACode);

      const network = await getFromStorage('currentAvoNetwork') || 'devnet';
      const provider = getAvoProvider(network);
      const signer = new ethers.Wallet(decryptedWalletJson.privateKey, provider);

      if (!gasOptions || !gasOptions[gasPriority]) {
        throw new Error('Gas options not available');
      }

      const sendAmount = maxMode ? calculateMaxSendable() : amount;

      if (parseFloat(sendAmount) <= 0) {
        throw new Error('Amount to send is insufficient after covering gas');
      }

      const txOptions = {
        maxFeePerGas: gasOptions[gasPriority].maxFeePerGas,
        maxPriorityFeePerGas: gasOptions[gasPriority].maxPriorityFeePerGas,
        gasLimit: gasOptions[gasPriority].gasLimit,
      };

      const tx = await sendAvoTransaction(
        signer,
        recipient,
        sendAmount,
        network,
        txOptions
      );

      if (!tx || !tx.hash) {
        throw new Error('Transaction did not return a valid hash');
      }

      toast.success(
        <Box>
          <Typography variant="body1">Transaction sent successfully!</Typography>
          <Typography variant="caption">
            Hash: {tx.hash.substring(0, 10)}...{tx.hash.substring(tx.hash.length - 4)}
          </Typography>
        </Box>,
        {
          position: 'top-center',
          autoClose: 8000,
          style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        }
      );

      setRecipient('');
      setAmount('');
      setTwoFACode('');
      setPassword('');
      setIsPasswordValid(false);
      setDecryptedWalletJson(null);
      setMaxMode(false);

      const newBalance = await getAvoBalance(wallet.address, network).catch(() => '0');
      setBalance(newBalance || '0');
    } catch (error) {
      console.error('Transaction error:', error);
      let errorMessage = error.message;
      if (error.code === 'UNPREDICTABLE_GAS_LIMIT') {
        errorMessage = 'Not enough AVO to cover gas. Get AVO tokens from the AVO network.';
      } else if (error.code === 'INSUFFICIENT_FUNDS') {
        errorMessage = 'Insufficient funds for transaction and gas. Get AVO tokens from the AVO network.';
      } else if (error.message.includes('API')) {
        errorMessage = 'API error: check your Alchemy key or try again later.';
      }
      toast.error(`Error: ${errorMessage}`, {
        position: 'top-center',
        autoClose: 5000,
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      toast.error('Please enter your wallet password.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
      return;
    }

    setIsProcessing(true);
    try {
      const encryptedWallet = await getFromStorage('cryptoWallet');
      if (!encryptedWallet) {
        throw new Error('Wallet not found in storage.');
      }
      await decryptData(encryptedWallet, deletePassword);

      await clearSecureStorage();
      setWallet(null);
      setIsSessionActive(false);
      await saveToStorage('sessionActive', { active: false, lastLogout: new Date().toISOString() });
      toast.success('Wallet permanently deleted.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        onClose: () => navigate('/create-wallet'),
      });
    } catch (err) {
      console.error('DEBUG: Delete error:', err);
      toast.error('Incorrect password or error deleting wallet.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
    } finally {
      setIsProcessing(false);
      setDeleteDialogOpen(false);
      setDeletePassword('');
    }
  };

  const handleLogout = async () => {
    try {
      await saveToStorage('sessionActive', { active: false, lastLogout: new Date().toISOString() });
      setWallet(null);
      setIsSessionActive(false);
      toast.success('Logged out successfully.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
        onClose: () => navigate('/login'),
      });
    } catch (err) {
      console.error('DEBUG: Logout error:', err);
      toast.error('Error logging out.', {
        position: 'top-center',
        style: { backgroundColor: '#121212', color: '#ffffff', borderRadius: '0', border: '1px solid #424242' },
      });
    }
  };

  const gasTrend = 'stable'; // Mocked: Can integrate with gas price API

  return (
    <ThemeProvider theme={strictMonochromeTheme}>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#000000',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Toast notifications */}
        <ToastContainer 
          limit={3} 
          newestOnTop 
          position="top-center" 
          autoClose={5000} 
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          style={{ zIndex: 1400 }}
          toastStyle={{
            backgroundColor: '#121212',
            color: '#ffffff',
            border: '1px solid #424242',
            borderRadius: '0',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
          }}
        />

      {/* Top bar */}
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: '#000000',
          borderBottom: '1px solid #FFFFFF',
          boxShadow: 'none',
          zIndex: 2
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 500,
            color: '#FFFFFF',
            fontSize: '1.4rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            letterSpacing: '2px',
            textTransform: 'uppercase'
          }}>
            AVO_STATION
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={wallet?.address ? `${wallet.address.substring(0, 6)}...${wallet.address.substring(38)}` : 'No Address'}
              clickable
              icon={<ContentCopy sx={{ color: '#FFFFFF' }} />}
              onClick={() => {
                if (wallet?.address) {
                  navigator.clipboard.writeText(wallet.address);
                  toast.success('Address copied to clipboard!', {
                    position: 'top-center',
                    style: { backgroundColor: '#000000', color: '#FFFFFF', borderRadius: '0', border: '1px solid #FFFFFF' },
                  });
                }
              }}
              sx={{ 
                bgcolor: '#000000', 
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                fontSize: '0.85rem',
                '&:hover': { bgcolor: '#FFFFFF', color: '#000000' },
                '& .MuiChip-icon': { color: '#FFFFFF' }
              }}
            />
            
            <Button
              onClick={() => navigate('/dashboard')}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              DASHBOARD
            </Button>
            <Button
              onClick={() => navigate('/history')}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              HISTORY
            </Button>
            <Button
              onClick={() => navigate('/backup')}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              SECURITY
            </Button>
            <Button
              onClick={() => navigate('/network')}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              NETWORK
            </Button>
            <Button
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              DELETE
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                borderRadius: 0,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: '#FFFFFF',
                  color: '#000000'
                }
              }}
            >
              LOG OUT
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Delete Account Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: '#121212', borderRadius: '0', border: '1px solid #424242' } }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1.4rem' }}>
          <Warning sx={{ verticalAlign: 'middle', mr: 1, color: '#ffffff' }} />
          Delete Account
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="#999999" sx={{ mb: 2, fontSize: '1.0rem' }}>
            This action will permanently delete your wallet. Enter your password to confirm.
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ 
                      color: '#ffffff',
                      '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
                      '&:focus': { backgroundColor: '#333333', color: '#cccccc' }
                    }}
                    disableRipple
                  >
                    {showPassword ? <VisibilityOff sx={{ fontSize: '20px' }} /> : <Visibility sx={{ fontSize: '20px' }} />}
                  </IconButton>
                </InputAdornment>
              ),
              startAdornment: (
                <InputAdornment position="start">
                  <Key sx={{ color: '#ffffff' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#222222',
                '& fieldset': { borderColor: '#333333' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#ffffff' },
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.0rem',
                color: '#999999',
              },
              '& .MuiInputBase-input': {
                fontSize: '1.0rem',
                padding: '12px',
                color: '#ffffff'
              }
            }}
          />
          <Typography variant="body2" color="#ffffff" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
            This action cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeletePassword('');
            }}
            sx={{ 
              color: '#ffffff', 
              fontSize: '1.0rem',
              '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
              '&:focus': { backgroundColor: '#333333', color: '#cccccc' }
            }}
          >
            Cancel
          </Button>
          <GradientLoadingButton
            onClick={handleDeleteAccount}
            variant="contained"
            disabled={!deletePassword || isProcessing}
            loading={isProcessing}
            sx={{ fontWeight: 600, fontSize: '1.0rem' }}
          >
            Delete
          </GradientLoadingButton>
        </DialogActions>
      </Dialog>

      {/* Confirm Transaction Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: '#121212', borderRadius: '0', border: '1px solid #424242' } }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1.4rem' }}>
          Confirm Transaction
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="#999999" sx={{ mb: 2, fontSize: '1.0rem' }}>
            You are about to send <strong>{amount} AVO</strong> to <strong>{recipient.substring(0, 6)}...{recipient.substring(38)}</strong>.
          </Typography>
          <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
            Estimated gas cost: {gasOptions && gasOptions[gasPriority] ? ethers.utils.formatEther(gasOptions[gasPriority].estimatedCost) : '0.00002'} AVO
          </Typography>
          <Typography variant="body2" color="#ffffff" sx={{ mt: 2, fontWeight: 500, fontSize: '0.9rem' }}>
            Verify the recipient address. Transactions are irreversible!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            sx={{ 
              color: '#ffffff', 
              fontSize: '1.0rem',
              '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
              '&:focus': { backgroundColor: '#333333', color: '#cccccc' }
            }}
          >
            Cancel
          </Button>
          <GradientLoadingButton
            onClick={confirmTransaction}
            variant="contained"
            loading={isProcessing}
            sx={{ fontWeight: 600, fontSize: '1.0rem' }}
          >
            Confirm
          </GradientLoadingButton>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        px: 2,
        py: 2,
        overflowY: 'auto',
        '-webkit-overflow-scrolling': 'touch'
      }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#ffffff', mb: 3, fontSize: '2.2rem' }}>
            Send AVO
          </Typography>

          {isWalletLoading ? (
            <Fade in={isWalletLoading}>
              <Card sx={{ textAlign: 'center', p: 3 }}>
                <CircularProgress sx={{ color: '#ffffff' }} />
                <Typography variant="body1" sx={{ mt: 2, color: '#999999', fontSize: '1.0rem' }}>
                  Loading wallet information...
                </Typography>
              </Card>
            </Fade>
          ) : !isWalletValid ? (
            <Card sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="body1" color="#ffffff" sx={{ fontSize: '1.0rem' }}>
                Invalid or missing wallet
              </Typography>
              <GradientButton
                variant="contained"
                onClick={() => navigate('/create-wallet')}
                startIcon={<Wallet />}
                sx={{ mt: 2, py: 1.5, fontWeight: 600, fontSize: '1.0rem' }}
              >
                Create Wallet
              </GradientButton>
            </Card>
          ) : (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Balance Card */}
                  <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
                          Available Balance
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.8rem' }}>
                          {parseFloat(balance || 0).toFixed(6)} AVO
                        </Typography>
                        <Typography variant="caption" color="#999999" sx={{ fontSize: '0.8rem' }}>
                          Network: {currentNetwork}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Transfer Details Card */}
                  <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', mb: 2, fontSize: '1.8rem' }}>
                          Transfer Details
                        </Typography>
                        <TextField
                          fullWidth
                          label="Recipient Address"
                          value={recipient}
                          onChange={(e) => setRecipient(e.target.value)}
                          placeholder="0x..."
                          error={recipient && !ethers.utils.isAddress(recipient)}
                          helperText={recipient && !ethers.utils.isAddress(recipient) ? 'Invalid AVO address' : ''}
                          InputProps={{ 
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person sx={{ color: '#ffffff' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              backgroundColor: '#222222',
                              '& fieldset': { borderColor: '#333333' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1.0rem',
                              color: '#999999',
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '1.0rem',
                              padding: '12px',
                              color: '#ffffff'
                            }
                          }}
                        />
                        <TextField
                          fullWidth
                          label="Amount to Send"
                          type="number"
                          value={amount}
                          onChange={(e) => {
                            setAmount(e.target.value);
                            setMaxMode(false);
                          }}
                          placeholder="0.0"
                          step="0.000001"
                          min="0"
                          error={amount && (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)}
                          helperText={
                            maxMode ? `Maximum sendable: ${calculateMaxSendable()} AVO` :
                              amount && isNaN(parseFloat(amount)) ? 'Invalid amount' :
                                amount && parseFloat(amount) <= 0 ? 'Amount must be positive' : ''
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CurrencyExchange sx={{ color: '#ffffff' }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={handleMaxClick}
                                startIcon={<AllInclusive sx={{ color: '#ffffff' }} />}
                                sx={{ 
                                  ml: 1, 
                                  borderColor: '#333333', 
                                  color: '#ffffff',
                                  borderRadius: '8px',
                                  '&:hover': { borderColor: '#666666', backgroundColor: '#333333' },
                                  '&:focus': { borderColor: '#666666', backgroundColor: '#333333' }
                                }}
                              >
                                MAX
                              </Button>
                            ),
                          }}
                          sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              backgroundColor: '#222222',
                              '& fieldset': { borderColor: '#333333' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1.0rem',
                              color: '#999999',
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '1.0rem',
                              padding: '12px',
                              color: '#ffffff'
                            }
                          }}
                        />

                        <Tooltip title="Choose gas priority based on desired speed and cost.">
                          <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel sx={{ color: '#999999', fontSize: '1.0rem', '&.Mui-focused': { color: '#ffffff' } }}>
                              Gas Priority
                            </InputLabel>
                            <Select
                              value={gasPriority}
                              label="Gas Priority"
                              onChange={(e) => setGasPriority(e.target.value)}
                              sx={{
                                borderRadius: '12px',
                                backgroundColor: '#222222',
                                '& .MuiSelect-select': { 
                                  padding: '12px', 
                                  fontSize: '1.0rem', 
                                  color: '#ffffff' 
                                },
                                '& .MuiOutlinedInput-notchedOutline': { 
                                  borderColor: '#333333' 
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': { 
                                  borderColor: '#666666' 
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                                  borderColor: '#ffffff' 
                                }
                              }}
                              startAdornment={(
                                <InputAdornment position="start">
                                  <Speed sx={{ color: '#ffffff' }} />
                                </InputAdornment>
                              )}
                              renderValue={(value) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', color: '#ffffff' }}>
                                  {value === 'low' && <Typography sx={{ color: '#ffffff', fontSize: '1.0rem' }}>Low</Typography>}
                                  {value === 'medium' && <Typography sx={{ color: '#ffffff', fontSize: '1.0rem' }}>Medium</Typography>}
                                  {value === 'high' && <Typography sx={{ color: '#ffffff', fontSize: '1.0rem' }}>High</Typography>}
                                </Box>
                              )}
                            >
                              <MenuItem value="low">
                                <Box sx={{ color: '#ffffff', fontSize: '1.0rem' }}>Low (Cheaper, slower)</Box>
                              </MenuItem>
                              <MenuItem value="medium">
                                <Box sx={{ color: '#ffffff', fontSize: '1.0rem' }}>Medium (Recommended)</Box>
                              </MenuItem>
                              <MenuItem value="high">
                                <Box sx={{ color: '#ffffff', fontSize: '1.0rem' }}>High (Faster, more expensive)</Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Tooltip>

                        {gasOptions && gasOptions[gasPriority] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            transition={{ duration: 0.3 }}
                          >
                            <Box sx={{ bgcolor: '#121212', p: 2, borderRadius: '0', mb: 2, border: '1px solid #424242' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
                                  Estimated gas cost: {ethers.utils.formatEther(gasOptions[gasPriority].estimatedCost)} AVO
                                  (~${(parseFloat(ethers.utils.formatEther(gasOptions[gasPriority].estimatedCost)) * avoPrice).toFixed(2)} USD)
                                </Typography>
                                <Chip
                                  icon={
                                    gasTrend === 'up' ? <TrendingUp sx={{ color: '#ffffff' }} /> :
                                    gasTrend === 'down' ? <TrendingDown sx={{ color: '#ffffff' }} /> :
                                    <TrendingFlat sx={{ color: '#ffffff' }} />
                                  }
                                  label={`Gas ${gasTrend === 'up' ? 'Rising' : gasTrend === 'down' ? 'Falling' : 'Stable'}`}
                                  size="small"
                                  sx={{ 
                                    bgcolor: '#333333', 
                                    color: '#ffffff',
                                    '&:hover': { bgcolor: '#666666', color: '#cccccc' }
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" color="#999999" sx={{ fontSize: '0.8rem' }}>
                                Max Fee: {ethers.utils.formatUnits(gasOptions[gasPriority].maxFeePerGas, 'gwei')} Gwei, 
                                Priority Fee: {ethers.utils.formatUnits(gasOptions[gasPriority].maxPriorityFeePerGas, 'gwei')} Gwei, 
                                Gas Limit: {gasOptions[gasPriority].gasLimit}
                              </Typography>
                            </Box>
                          </motion.div>
                        )}

                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', mb: 2, fontSize: '1.8rem' }}>
                          Security Verification
                        </Typography>
                        <TextField
                          fullWidth
                          label="Wallet Password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          InputProps={{ 
                            startAdornment: (
                              <InputAdornment position="start">
                                <Key sx={{ color: '#ffffff' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              backgroundColor: '#222222',
                              '& fieldset': { borderColor: '#333333' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '1.0rem',
                              color: '#999999',
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '1.0rem',
                              padding: '12px',
                              color: '#ffffff'
                            }
                          }}
                        />
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <GradientButton
                            variant="contained"
                            onClick={validatePassword}
                            disabled={!password || isProcessing}
                            fullWidth
                            sx={{ py: 1.5, fontWeight: 600, fontSize: '1.0rem', mb: 2 }}
                          >
                            Validate Password
                          </GradientButton>
                        </motion.div>
                        {isPasswordValid && (
                          <TextField
                            fullWidth
                            label="2FA Code"
                            value={twoFACode}
                            onChange={(e) => /^\d*$/.test(e.target.value) && setTwoFACode(e.target.value)}
                            placeholder="123456"
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                            error={twoFACode && !/^\d{6}$/.test(twoFACode)}
                            helperText={twoFACode && !/^\d{6}$/.test(twoFACode) ? 'Must be a 6-digit code' : ''}
                            InputProps={{ 
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Lock sx={{ color: '#ffffff' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ 
                              mb: 2,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                backgroundColor: '#222222',
                                '& fieldset': { borderColor: '#333333' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                              },
                              '& .MuiInputLabel-root': {
                                fontSize: '1.0rem',
                                color: '#999999',
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '1.0rem',
                                padding: '12px',
                                color: '#ffffff'
                              }
                            }}
                          />
                        )}
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <GradientLoadingButton
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSend}
                            disabled={isProcessing || !recipient || !amount || !isPasswordValid || !twoFACode}
                            loading={isProcessing}
                            fullWidth
                            sx={{ py: 1.5, fontWeight: 600, fontSize: '1.0rem' }}
                          >
                            Confirm and Send
                          </GradientLoadingButton>
                        </motion.div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Important Information Card */}
                  <motion.div
                    initial={{ scale: 0.95 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', mb: 2, fontSize: '1.8rem' }}>
                          Important Information
                        </Typography>
                        <List>
                          <ListItem sx={{ py: 1 }}>
                            <ListItemIcon><Info sx={{ color: '#ffffff' }} /></ListItemIcon>
                            <ListItemText primary="Transactions are irreversible - verify the recipient address" sx={{ '& .MuiListItemText-primary': { color: '#ffffff', fontSize: '1.0rem' } }} />
                          </ListItem>
                          <ListItem sx={{ py: 1 }}>
                            <ListItemIcon><Info sx={{ color: '#ffffff' }} /></ListItemIcon>
                            <ListItemText primary="Typical confirmation time: 1-5 minutes" sx={{ '& .MuiListItemText-primary': { color: '#ffffff', fontSize: '1.0rem' } }} />
                          </ListItem>
                          <ListItem sx={{ py: 1 }}>
                            <ListItemIcon><Info sx={{ color: '#ffffff' }} /></ListItemIcon>
                            <ListItemText primary="Network fees will be deducted from your balance" sx={{ '& .MuiListItemText-primary': { color: '#ffffff', fontSize: '1.0rem' } }} />
                          </ListItem>
                          <ListItem sx={{ py: 1 }}>
                            <ListItemIcon><Info sx={{ color: '#ffffff' }} /></ListItemIcon>
                            <ListItemText primary="Never share your password or 2FA code" sx={{ '& .MuiListItemText-primary': { color: '#ffffff', fontSize: '1.0rem' } }} />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Box>
              </motion.div>
            </AnimatePresence>
          )}
        </motion.div>
      </Box>
    </Box>
    </ThemeProvider>
  );
}

export default Send;
